import { CampanhaSimulada, SugestaoEvolutiva } from '../types/simulador';

// Simula análise de campanhas anteriores
const campanhasHistoricas: CampanhaSimulada[] = [];

// Armazenamento de feedback de campanhas
interface FeedbackCampanha {
  campanhaId: string;
  taxaEngajamento: number; // 0-100
  taxaConclusao: number; // 0-100
  roi: number; // Retorno sobre investimento
  satisfacao: number; // 1-5
  observacoes: string;
  dataFeedback: string;
}

const feedbackHistorico: FeedbackCampanha[] = [];

export const adicionarCampanhaAoHistorico = (campanha: CampanhaSimulada) => {
  campanhasHistoricas.push(campanha);
};

export const adicionarFeedback = (feedback: FeedbackCampanha) => {
  feedbackHistorico.push(feedback);
};

// NOVA FUNCIONALIDADE: Previsão de Engajamento
export const preverEngajamento = (campanha: CampanhaSimulada): {
  taxaEngajamentoEsperada: number;
  confianca: 'baixa' | 'media' | 'alta';
  fatoresPositivos: string[];
  fatoresNegativos: string[];
} => {
  let pontuacao = 50; // Base
  const fatoresPositivos: string[] = [];
  const fatoresNegativos: string[] = [];

  // Análise de complexidade
  if (campanha.analiseComplexidade.nivelComplexidade === 'Simples') {
    pontuacao += 15;
    fatoresPositivos.push('Campanha simples e fácil de entender');
  } else if (campanha.analiseComplexidade.nivelComplexidade === 'Muito Complexa') {
    pontuacao -= 15;
    fatoresNegativos.push('Campanha muito complexa pode confundir participantes');
  }

  // Análise de recompensas
  const custoMedio = campanha.analiseCustos.custoMedioPorParticipante;
  if (custoMedio > 1000) {
    pontuacao += 20;
    fatoresPositivos.push('Recompensas atrativas aumentam motivação');
  } else if (custoMedio < 300) {
    pontuacao -= 10;
    fatoresNegativos.push('Recompensas podem ser pouco atrativas');
  }

  // Análise de gamificação
  const totalDistintivos = campanha.distintivos.length;
  const totalMissoes = campanha.missoes.length;
  
  if (totalDistintivos >= 6 && totalMissoes >= 8) {
    pontuacao += 10;
    fatoresPositivos.push('Boa variedade de distintivos e missões');
  } else if (totalDistintivos < 3 || totalMissoes < 4) {
    pontuacao -= 8;
    fatoresNegativos.push('Poucos elementos de gamificação');
  }

  // Análise de metas
  const metasPorNivel = campanha.metas.reduce((acc, m) => {
    acc[m.nivel] = (acc[m.nivel] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const niveisComMetas = Object.keys(metasPorNivel).length;
  if (niveisComMetas >= 4) {
    pontuacao += 8;
    fatoresPositivos.push('Metas bem distribuídas entre os níveis');
  } else if (niveisComMetas < 2) {
    pontuacao -= 10;
    fatoresNegativos.push('Metas concentradas em poucos níveis');
  }

  // Análise de produtos
  const produtosEstrategicos = campanha.produtosServicos.filter(p => p.pesoComercial === 'Estratégico').length;
  if (produtosEstrategicos > 0) {
    pontuacao += 5;
    fatoresPositivos.push('Produtos estratégicos com alto incentivo');
  }

  // Análise de duração
  const inicio = new Date(campanha.dataInicio);
  const fim = new Date(campanha.dataFim);
  const duracaoDias = Math.ceil((fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
  
  if (duracaoDias >= 30 && duracaoDias <= 90) {
    pontuacao += 5;
    fatoresPositivos.push('Duração ideal para manter engajamento');
  } else if (duracaoDias < 15) {
    pontuacao -= 8;
    fatoresNegativos.push('Campanha muito curta pode limitar resultados');
  } else if (duracaoDias > 180) {
    pontuacao -= 5;
    fatoresNegativos.push('Campanha muito longa pode perder engajamento');
  }

  // Aprendizado com histórico
  if (feedbackHistorico.length > 0) {
    const mediaEngajamentoHistorico = feedbackHistorico.reduce((acc, f) => acc + f.taxaEngajamento, 0) / feedbackHistorico.length;
    
    // Ajusta baseado em padrões históricos
    if (campanha.analiseComplexidade.indiceComplexidade < 50) {
      pontuacao += (mediaEngajamentoHistorico - 50) * 0.2;
    }
  }

  // Normaliza pontuação
  pontuacao = Math.max(10, Math.min(95, pontuacao));

  // Define confiança baseado em histórico
  let confianca: 'baixa' | 'media' | 'alta';
  if (feedbackHistorico.length < 5) {
    confianca = 'baixa';
  } else if (feedbackHistorico.length < 20) {
    confianca = 'media';
  } else {
    confianca = 'alta';
  }

  return {
    taxaEngajamentoEsperada: Math.round(pontuacao),
    confianca,
    fatoresPositivos,
    fatoresNegativos
  };
};

// NOVA FUNCIONALIDADE: Cálculo de ROI Esperado
export const calcularROIEsperado = (campanha: CampanhaSimulada): {
  roiEsperado: number;
  receitaEstimada: number;
  custoTotal: number;
  lucroEstimado: number;
  breakEvenParticipantes: number;
  analise: string;
} => {
  const custoTotal = campanha.analiseCustos.custoTotalRecompensas;
  
  // Estima receita baseado em produtos e participantes
  let receitaEstimada = 0;
  
  campanha.produtosServicos.forEach(produto => {
    const valorMedio = produto.valorReferencia || 100;
    const vendasEstimadas = campanha.participantes.length * 0.3; // 30% dos participantes vendem
    const volumeMedio = 5; // Volume médio por participante ativo
    
    receitaEstimada += valorMedio * vendasEstimadas * volumeMedio;
  });

  // Ajusta baseado em engajamento previsto
  const previsaoEngajamento = preverEngajamento(campanha);
  const fatorEngajamento = previsaoEngajamento.taxaEngajamentoEsperada / 100;
  receitaEstimada *= fatorEngajamento;

  const lucroEstimado = receitaEstimada - custoTotal;
  const roiEsperado = custoTotal > 0 ? (lucroEstimado / custoTotal) * 100 : 0;

  // Calcula break-even
  const custoMedioPorParticipante = campanha.analiseCustos.custoMedioPorParticipante;
  const receitaMediaPorParticipante = campanha.participantes.length > 0 
    ? receitaEstimada / campanha.participantes.length 
    : 0;
  
  const breakEvenParticipantes = receitaMediaPorParticipante > 0
    ? Math.ceil(custoTotal / receitaMediaPorParticipante)
    : campanha.participantes.length;

  // Análise qualitativa
  let analise = '';
  if (roiEsperado > 200) {
    analise = 'Excelente! ROI muito positivo esperado.';
  } else if (roiEsperado > 100) {
    analise = 'Bom! ROI positivo esperado.';
  } else if (roiEsperado > 0) {
    analise = 'Moderado. ROI positivo mas pode ser otimizado.';
  } else {
    analise = 'Atenção! ROI negativo. Revise custos e estratégia.';
  }

  return {
    roiEsperado: Math.round(roiEsperado),
    receitaEstimada: Math.round(receitaEstimada),
    custoTotal: Math.round(custoTotal),
    lucroEstimado: Math.round(lucroEstimado),
    breakEvenParticipantes,
    analise
  };
};

// NOVA FUNCIONALIDADE: Análise de Risco
export const analisarRiscos = (campanha: CampanhaSimulada): {
  nivelRisco: 'baixo' | 'medio' | 'alto';
  riscos: Array<{ tipo: string; descricao: string; severidade: 'baixa' | 'media' | 'alta' }>;
  recomendacoes: string[];
} => {
  const riscos: Array<{ tipo: string; descricao: string; severidade: 'baixa' | 'media' | 'alta' }> = [];
  const recomendacoes: string[] = [];
  let pontuacaoRisco = 0;

  // Risco de complexidade
  if (campanha.analiseComplexidade.nivelComplexidade === 'Muito Complexa') {
    riscos.push({
      tipo: 'Complexidade',
      descricao: 'Campanha muito complexa pode reduzir engajamento',
      severidade: 'alta'
    });
    pontuacaoRisco += 30;
    recomendacoes.push('Simplifique as regras e reduza o número de metas');
  }

  // Risco de custo
  const roi = calcularROIEsperado(campanha);
  if (roi.roiEsperado < 0) {
    riscos.push({
      tipo: 'Financeiro',
      descricao: 'ROI negativo esperado',
      severidade: 'alta'
    });
    pontuacaoRisco += 35;
    recomendacoes.push('Revise os custos das recompensas ou aumente as metas de vendas');
  } else if (roi.roiEsperado < 50) {
    riscos.push({
      tipo: 'Financeiro',
      descricao: 'ROI baixo esperado',
      severidade: 'media'
    });
    pontuacaoRisco += 20;
    recomendacoes.push('Otimize a relação custo-benefício das recompensas');
  }

  // Risco de engajamento
  const previsao = preverEngajamento(campanha);
  if (previsao.taxaEngajamentoEsperada < 40) {
    riscos.push({
      tipo: 'Engajamento',
      descricao: 'Baixa taxa de engajamento esperada',
      severidade: 'alta'
    });
    pontuacaoRisco += 25;
    recomendacoes.push('Adicione mais elementos de gamificação e recompensas atrativas');
  }

  // Risco de duração
  const inicio = new Date(campanha.dataInicio);
  const fim = new Date(campanha.dataFim);
  const duracaoDias = Math.ceil((fim.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
  
  if (duracaoDias < 15) {
    riscos.push({
      tipo: 'Duração',
      descricao: 'Campanha muito curta pode limitar resultados',
      severidade: 'media'
    });
    pontuacaoRisco += 15;
    recomendacoes.push('Considere estender a duração para pelo menos 30 dias');
  }

  // Risco de participantes
  if (campanha.participantes.length < 10) {
    riscos.push({
      tipo: 'Participantes',
      descricao: 'Poucos participantes podem não justificar o investimento',
      severidade: 'media'
    });
    pontuacaoRisco += 15;
    recomendacoes.push('Adicione mais participantes para diluir custos fixos');
  }

  // Risco de gamificação
  if (campanha.distintivos.length < 4 || campanha.missoes.length < 6) {
    riscos.push({
      tipo: 'Gamificação',
      descricao: 'Poucos elementos de gamificação podem reduzir engajamento',
      severidade: 'baixa'
    });
    pontuacaoRisco += 10;
    recomendacoes.push('Adicione mais distintivos e missões para manter interesse');
  }

  // Define nível de risco
  let nivelRisco: 'baixo' | 'medio' | 'alto';
  if (pontuacaoRisco < 30) {
    nivelRisco = 'baixo';
  } else if (pontuacaoRisco < 60) {
    nivelRisco = 'medio';
  } else {
    nivelRisco = 'alto';
  }

  return {
    nivelRisco,
    riscos,
    recomendacoes
  };
};

// NOVA FUNCIONALIDADE: Simulação de Cenários
export const gerarCenariosAlternativos = (campanhaBase: CampanhaSimulada): {
  conservador: { descricao: string; ajustes: string[]; impacto: string };
  equilibrado: { descricao: string; ajustes: string[]; impacto: string };
  agressivo: { descricao: string; ajustes: string[]; impacto: string };
} => {
  return {
    conservador: {
      descricao: 'Cenário focado em redução de custos e risco mínimo',
      ajustes: [
        'Reduzir recompensas dos níveis iniciais em 30%',
        'Focar em distintivos e reconhecimento',
        'Metas mais conservadoras (+20% mais fáceis)',
        'Menos missões diárias, mais semanais'
      ],
      impacto: 'Custo -35%, Engajamento -15%, ROI +25%'
    },
    equilibrado: {
      descricao: 'Cenário balanceado entre custo e engajamento (atual)',
      ajustes: [
        'Manter estrutura atual',
        'Pequenos ajustes baseados em análise',
        'Equilíbrio entre pontos e XP',
        'Mix de recompensas tangíveis e intangíveis'
      ],
      impacto: 'Custo base, Engajamento base, ROI base'
    },
    agressivo: {
      descricao: 'Cenário focado em máximo engajamento e resultados',
      ajustes: [
        'Aumentar recompensas em 40%',
        'Adicionar bônus por performance',
        'Metas mais desafiadoras (-15% mais difíceis)',
        'Mais missões diárias e distintivos exclusivos'
      ],
      impacto: 'Custo +45%, Engajamento +30%, ROI +15%'
    }
  };
};

export const analisarPadroesDeEngajamento = (campanha: CampanhaSimulada): SugestaoEvolutiva[] => {
  const sugestoes: SugestaoEvolutiva[] = [];
  let idCounter = 1;

  // Análise de complexidade
  if (campanha.analiseComplexidade.nivelComplexidade === 'Muito Complexa') {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'simplificacao',
      titulo: 'Campanha muito complexa',
      descricao: 'Considere reduzir o número de metas e missões para facilitar o engajamento dos participantes.',
      impacto: 'alto',
      baseadoEm: 'Análise de complexidade'
    });
  }

  // Análise de custos
  if (campanha.analiseCustos.custoMedioPorParticipante > 2000) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'otimizacao',
      titulo: 'Custo elevado por participante',
      descricao: 'O custo médio por participante está acima da média. Considere ajustar as recompensas dos níveis iniciais.',
      impacto: 'medio',
      baseadoEm: 'Análise de custos'
    });
  }

  // NOVA: Análise de ROI
  const roi = calcularROIEsperado(campanha);
  if (roi.roiEsperado < 50) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'otimizacao',
      titulo: 'ROI abaixo do ideal',
      descricao: `ROI esperado de ${roi.roiEsperado}% está abaixo do ideal. ${roi.analise}`,
      impacto: 'alto',
      baseadoEm: 'Análise de ROI'
    });
  }

  // NOVA: Análise de engajamento
  const previsao = preverEngajamento(campanha);
  if (previsao.taxaEngajamentoEsperada < 50) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'melhoria',
      titulo: 'Engajamento previsto abaixo da média',
      descricao: `Taxa de engajamento esperada: ${previsao.taxaEngajamentoEsperada}%. Considere: ${previsao.fatoresNegativos.join(', ')}`,
      impacto: 'alto',
      baseadoEm: 'Previsão de engajamento'
    });
  }

  // Análise de distribuição de recompensas
  const recompensasPorNivel = campanha.niveis.map(n => n.recompensas.length);
  const mediaRecompensas = recompensasPorNivel.reduce((a, b) => a + b, 0) / recompensasPorNivel.length;
  
  if (Math.max(...recompensasPorNivel) > mediaRecompensas * 2) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'melhoria',
      titulo: 'Distribuição desigual de recompensas',
      descricao: 'Alguns níveis têm muito mais recompensas que outros. Considere equilibrar a distribuição.',
      impacto: 'medio',
      baseadoEm: 'Análise de distribuição'
    });
  }

  // Análise de metas
  if (campanha.metas.length < 5) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'melhoria',
      titulo: 'Poucas metas definidas',
      descricao: 'Adicione mais metas intermediárias para manter os participantes engajados ao longo da campanha.',
      impacto: 'medio',
      baseadoEm: 'Análise de metas'
    });
  }

  // Análise de distintivos
  if (campanha.distintivos.length < 4) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'melhoria',
      titulo: 'Poucos distintivos',
      descricao: 'Distintivos aumentam o engajamento. Considere adicionar mais distintivos para reconhecer diferentes conquistas.',
      impacto: 'baixo',
      baseadoEm: 'Análise de gamificação'
    });
  }

  // Análise de missões
  const missoesDiarias = campanha.missoes.filter(m => m.tipo === 'diaria').length;
  if (missoesDiarias < 2) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'melhoria',
      titulo: 'Poucas missões diárias',
      descricao: 'Missões diárias mantêm os participantes engajados. Adicione pelo menos 2-3 missões diárias.',
      impacto: 'medio',
      baseadoEm: 'Análise de engajamento'
    });
  }

  // Comparação com campanhas históricas
  if (campanhasHistoricas.length > 0) {
    const custoMedioHistorico = campanhasHistoricas.reduce((acc, c) => 
      acc + c.analiseCustos.custoMedioPorParticipante, 0
    ) / campanhasHistoricas.length;

    if (campanha.analiseCustos.custoMedioPorParticipante < custoMedioHistorico * 0.5) {
      sugestoes.push({
        id: `sug-${idCounter++}`,
        tipo: 'alerta',
        titulo: 'Investimento abaixo da média',
        descricao: 'Esta campanha tem investimento significativamente menor que campanhas anteriores. Isso pode afetar o engajamento.',
        impacto: 'alto',
        baseadoEm: 'Comparação com histórico'
      });
    }
  }

  // Sugestão de produtos estratégicos
  const produtosEstrategicos = campanha.produtosServicos.filter(p => p.pesoComercial === 'Estratégico');
  if (produtosEstrategicos.length > 0 && campanha.distintivos.filter(d => d.nome.includes('Estratégico')).length === 0) {
    sugestoes.push({
      id: `sug-${idCounter++}`,
      tipo: 'melhoria',
      titulo: 'Destaque produtos estratégicos',
      descricao: 'Você tem produtos estratégicos na campanha. Considere criar distintivos específicos para incentivar suas vendas.',
      impacto: 'alto',
      baseadoEm: 'Análise de produtos'
    });
  }

  return sugestoes;
};

export const compararComCampanhasSimilares = (campanha: CampanhaSimulada): string[] => {
  const insights: string[] = [];

  if (campanhasHistoricas.length === 0) {
    insights.push('Esta é sua primeira simulação. Continue criando campanhas para receber insights comparativos.');
    return insights;
  }

  const campanhasSimilares = campanhasHistoricas.filter(c => c.tipo === campanha.tipo);

  if (campanhasSimilares.length > 0) {
    const mediaParticipantes = campanhasSimilares.reduce((acc, c) => 
      acc + c.participantes.length, 0
    ) / campanhasSimilares.length;

    if (campanha.participantes.length > mediaParticipantes * 1.5) {
      insights.push(`Esta campanha tem ${Math.round((campanha.participantes.length / mediaParticipantes - 1) * 100)}% mais participantes que campanhas similares anteriores.`);
    }

    const mediaProdutos = campanhasSimilares.reduce((acc, c) => 
      acc + c.produtosServicos.length, 0
    ) / campanhasSimilares.length;

    if (campanha.produtosServicos.length < mediaProdutos * 0.7) {
      insights.push('Esta campanha tem menos produtos que o usual. Considere adicionar mais produtos para aumentar as oportunidades de venda.');
    }

    // NOVO: Comparação de ROI
    const roisHistoricos = campanhasSimilares.map(c => calcularROIEsperado(c).roiEsperado);
    const mediaROI = roisHistoricos.reduce((a, b) => a + b, 0) / roisHistoricos.length;
    const roiAtual = calcularROIEsperado(campanha).roiEsperado;

    if (roiAtual > mediaROI * 1.2) {
      insights.push(`Excelente! ROI esperado ${Math.round((roiAtual / mediaROI - 1) * 100)}% acima da média histórica.`);
    } else if (roiAtual < mediaROI * 0.8) {
      insights.push(`Atenção: ROI esperado ${Math.round((1 - roiAtual / mediaROI) * 100)}% abaixo da média histórica.`);
    }
  }

  return insights;
};

export const obterRecomendacoesEvolutivas = (campanha: CampanhaSimulada): SugestaoEvolutiva[] => {
  const sugestoes = analisarPadroesDeEngajamento(campanha);
  return sugestoes.sort((a, b) => {
    const impactoOrder = { alto: 3, medio: 2, baixo: 1 };
    return impactoOrder[b.impacto] - impactoOrder[a.impacto];
  });
};

export const obterEstatisticasHistoricas = () => {
  if (campanhasHistoricas.length === 0) {
    return null;
  }

  const roiMedio = campanhasHistoricas.reduce((acc, c) => {
    return acc + calcularROIEsperado(c).roiEsperado;
  }, 0) / campanhasHistoricas.length;

  const engajamentoMedio = campanhasHistoricas.reduce((acc, c) => {
    return acc + preverEngajamento(c).taxaEngajamentoEsperada;
  }, 0) / campanhasHistoricas.length;

  return {
    totalCampanhas: campanhasHistoricas.length,
    custoMedio: campanhasHistoricas.reduce((acc, c) => 
      acc + c.analiseCustos.custoTotalRecompensas, 0
    ) / campanhasHistoricas.length,
    participantesMedio: campanhasHistoricas.reduce((acc, c) => 
      acc + c.participantes.length, 0
    ) / campanhasHistoricas.length,
    complexidadeMedia: campanhasHistoricas.reduce((acc, c) => 
      acc + c.analiseComplexidade.indiceComplexidade, 0
    ) / campanhasHistoricas.length,
    roiMedio,
    engajamentoMedio,
    totalFeedbacks: feedbackHistorico.length
  };
};

// NOVA FUNCIONALIDADE: Análise Completa
export const analisarCampanhaCompleta = (campanha: CampanhaSimulada) => {
  return {
    previsaoEngajamento: preverEngajamento(campanha),
    analiseROI: calcularROIEsperado(campanha),
    analiseRiscos: analisarRiscos(campanha),
    cenarios: gerarCenariosAlternativos(campanha),
    sugestoes: obterRecomendacoesEvolutivas(campanha),
    comparacaoHistorica: compararComCampanhasSimilares(campanha),
    estatisticasHistoricas: obterEstatisticasHistoricas()
  };
};
